import { useEffect, useRef } from 'react';

interface UsePageResumeOptions {
	enabled?: boolean;
	minIntervalMs?: number;
}

export function usePageResume(callback: () => void, options: UsePageResumeOptions = {}) {
	const { enabled = true, minIntervalMs = 1000 } = options;
	const callbackRef = useRef(callback);
	const lastRunAtRef = useRef(0);

	useEffect(() => {
		callbackRef.current = callback;
	}, [callback]);

	useEffect(() => {
		if (!enabled || typeof window === 'undefined' || typeof document === 'undefined') {
			return;
		}

		const run = () => {
			const now = Date.now();

			if (now - lastRunAtRef.current < minIntervalMs) {
				return;
			}

			lastRunAtRef.current = now;
			callbackRef.current?.();
		};

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				run();
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		window.addEventListener('pageshow', run);
		window.addEventListener('focus', run);
		window.addEventListener('online', run);

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			window.removeEventListener('pageshow', run);
			window.removeEventListener('focus', run);
			window.removeEventListener('online', run);
		};
	}, [enabled, minIntervalMs]);
}
